import { createContext, useContext, useMemo } from 'react';

/**
 * Le domaine d'arrivée décide du périmètre visible.
 *
 * Un sous-domaine d'app (bibliotheque.cabaza.eu) ne montre que son module ;
 * un domaine de plateforme (www, fr, nl) montre tout. C'est la seule
 * différence, et elle tient à une donnée : domains.moduleId.
 *
 * Le contexte ne charge rien lui-même. App.jsx appelle déjà
 * getDomainConfig(hostname) au démarrage et attend sa réponse avant de rendre
 * quoi que ce soit — dupliquer cet appel ferait deux requêtes pour une seule
 * information.
 */

const DomainContext = createContext({
    moduleCode: null,
    isApp: false,
    app: { name: '', iconPath: '', themeColor: '' },
    isLoading: false,
    hasError: false
});

/**
 * Extrait le code du module d'une configuration de domaine.
 *
 * ColdFusion rend ses clés en minuscules, mais la casse varie selon le
 * chemin de sérialisation : les trois formes sont donc lues.
 *
 * Rend null plutôt qu'une chaîne vide : null se teste sans ambiguïté, et
 * distingue « pas de module » de « module au nom vide », qui serait un bug.
 *
 * @param {object|null} config - La configuration renvoyée par l'API
 * @returns {string|null} Le code du module, ou null pour un domaine de plateforme
 */
export function resolveDomainModule(config) {
    if (!config) return null;

    const brut = config.moduleCode ?? config.modulecode ?? config.MODULECODE ?? '';
    const code = String(brut).trim();

    if (!code) return null;
    return code.toUpperCase();
}

/**
 * Fournit le périmètre du domaine à l'arbre React.
 *
 * Les trois états sont distincts, et ce n'est pas un détail : les confondre
 * reproduirait le défaut des replis permissifs, où un état inconnu était
 * traité comme un état permissif. Sur un sous-domaine, ne pas savoir n'est
 * pas une raison de tout montrer.
 *
 * @param {object} props
 * @param {object|null} props.domainConfig - Configuration chargée par App.jsx
 * @param {boolean} props.isLoading - La configuration est encore en vol
 * @param {boolean} props.hasError - La configuration n'a pas pu être obtenue
 */
export function DomainProvider({ domainConfig, isLoading = false, hasError = false, children }) {
    const valeur = useMemo(() => {
        const moduleCode = resolveDomainModule(domainConfig);
        return {
            moduleCode,
            isApp: moduleCode !== null,
            // Identité de l'application autonome (admin) : vide sur la plateforme
            app: moduleCode === null
                ? { name: '', iconPath: '', themeColor: '' }
                : {
                    name: domainConfig?.appName ?? '',
                    iconPath: domainConfig?.appIconPath ?? '',
                    themeColor: domainConfig?.appThemeColor ?? ''
                },
            isLoading,
            hasError
        };
    }, [domainConfig, isLoading, hasError]);

    return (
        <DomainContext.Provider value={valeur}>
            {children}
        </DomainContext.Provider>
    );
}

export function useDomain() {
    return useContext(DomainContext);
}

export default DomainContext;
